import { queryOptions, useQuery } from "@tanstack/react-query";
import { parseResponse } from "lib/fetch";
import type { StripeAccount, StripeAccountLink } from "types/stripe";
import {
    getStripeAccountInfoRequest,
    getStripeRedirectionAccountLinkRequest,
} from "lib/api/stripe";

export const fetchStripeRedirectionAccountLink = (accountStripeId: string) =>
    parseResponse<StripeAccountLink>(getStripeRedirectionAccountLinkRequest(accountStripeId));

export const fetchStripeAccountInfo = (accountStripeId: string) =>
    parseResponse<StripeAccount>(getStripeAccountInfoRequest(accountStripeId));

export const stripeKeys = {
    all: ["stripe"] as const,
    redirection: (accountStripeId: string) => [...stripeKeys.all, "redirection", accountStripeId] as const,
    account: (accountStripeId: string) => [...stripeKeys.all, "account", accountStripeId] as const,
};

export const stripeRedirectionQueryOptions = (accountStripeId: string | undefined) =>
    queryOptions<StripeAccountLink>({
        queryKey: accountStripeId
            ? stripeKeys.redirection(accountStripeId)
            : stripeKeys.redirection("__missing__"),
        queryFn: () => {
            if (!accountStripeId) {
                return Promise.reject(new Error("Missing stripe account id"));
            }
            return fetchStripeRedirectionAccountLink(accountStripeId);
        },
        enabled: Boolean(accountStripeId),
    });

export const useStripeRedirectionAccountLink = (accountStripeId: string | undefined) =>
    useQuery(stripeRedirectionQueryOptions(accountStripeId));

export const stripeAccountQueryOptions = (accountStripeId: string | undefined) =>
    queryOptions<StripeAccount>({
        queryKey: accountStripeId ? stripeKeys.account(accountStripeId) : stripeKeys.account("__missing__"),
        queryFn: () => {
            if (!accountStripeId) {
                return Promise.reject(new Error("Missing stripe account id"));
            }
            return fetchStripeAccountInfo(accountStripeId);
        },
        enabled: Boolean(accountStripeId),
    });

export const useStripeAccount = (accountStripeId: string | undefined) =>
    useQuery(stripeAccountQueryOptions(accountStripeId));
